import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Grid,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import {
  ArrowUpDown,
  Pencil,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { articlesApi } from '@api/articles';
import { categoriesApi } from '@api/categories';
import { tagsApi } from '@api/tags';
import { authorsApi } from '@api/authors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useAuth } from '@features/auth/hooks/useAuth';
import { ROUTES } from '@shared/constants/routes';
import { FeaturedReorderModal } from '../components/FeaturedReorderModal';
import {
  ARTICLE_FILTER_STATUSES,
  toContentStatus,
  useArticleStatusLabel,
  type ArticleListItem,
  type ArticleStatus,
  type BulkArticleAction,
} from '../types';

type StatusFilter = ArticleStatus | 'ALL';

/**
 * S9 — Articles list page wired to the real backend.
 *
 * RBAC: the BE permission model uses `content.write` / `content.publish`,
 * which we cannot 1:1 map yet. As documented in the Foundation handoff,
 * we gate publish-grade actions on `user.role === 'ADMIN'` (SuperAdmin/Admin
 * collapse to ADMIN in the FE).
 */
export function ArticleListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const statusLabel = useArticleStatusLabel();
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [authorId, setAuthorId] = useState<string | undefined>();
  const [tagId, setTagId] = useState<string | undefined>();
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [languageId, setLanguageId] = useState<string | undefined>();
  const [reorderOpen, setReorderOpen] = useState(false);
  const [pendingStarId, setPendingStarId] = useState<string | null>(null);

  const hasActiveFilters =
    search !== '' ||
    status !== 'ALL' ||
    !!categoryId ||
    !!authorId ||
    !!tagId ||
    featuredOnly ||
    publishedOnly ||
    !!languageId;

  const resetFilters = () => {
    setSearch('');
    setStatus('ALL');
    setCategoryId(undefined);
    setAuthorId(undefined);
    setTagId(undefined);
    setFeaturedOnly(false);
    setPublishedOnly(false);
    setLanguageId(undefined);
    setPage(1);
  };

  const { data, isFetching } = useQuery({
    queryKey: [
      'articles',
      {
        page,
        pageSize,
        search: debouncedSearch,
        status,
        categoryId,
        authorId,
        tagId,
        featuredOnly,
        publishedOnly,
        languageId,
      },
    ],
    queryFn: () =>
      articlesApi.list({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        status: status === 'ALL' ? undefined : status,
        categoryId,
        authorId,
        tagId,
        featuredOnly: featuredOnly || undefined,
        publishedOnly: publishedOnly || undefined,
        languageId,
        sortBy: 'updatedAt',
      }),
    placeholderData: (prev) => prev,
  });

  // Related entity lookups — these endpoints are owned by other agents and
  // may not exist yet. We swallow errors so this page renders regardless.
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', { scope: 'ARTICLE' }],
    queryFn: () => categoriesApi.list({ scope: 'ARTICLE' }),
    retry: false,
  });
  const categoryMap = useMemo(() => {
    const m = new Map<string, string>();
    (categoriesData?.items ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [categoriesData]);

  const { data: authorsData } = useQuery({
    queryKey: ['authors', 'list'],
    queryFn: () => authorsApi.list({ pageSize: 100 }),
    retry: false,
  });
  const authorMap = useMemo(() => {
    const m = new Map<string, string>();
    (authorsData?.items ?? []).forEach((a) => m.set(a.id, a.displayName));
    return m;
  }, [authorsData]);

  const { data: tagsData } = useQuery({
    queryKey: ['tags', 'list-short'],
    queryFn: () => tagsApi.list({ pageSize: 30 }),
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: (a: ArticleListItem) => articlesApi.remove(a.id),
    onSuccess: async (_d, a) => {
      toast.success(t('toast.deleted', { title: a.title }));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      void recordAuditAction({
        action: 'article.delete',
        resource: 'article',
        resourceId: a.id,
        summary: t('audit.deleted', { title: a.title }),
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const bulkMutation = useMutation({
    mutationFn: (vars: { action: BulkArticleAction; ids: string[] }) =>
      articlesApi.bulk(vars.action, vars.ids),
    onSuccess: async (applied, vars) => {
      toast.success(t('toast.bulkApplied', { count: applied, action: vars.action }));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      await qc.invalidateQueries({ queryKey: ['articles-featured'] });
      void recordAuditAction({
        action:
          vars.action === 'publish'
            ? 'article.publish'
            : vars.action === 'archive'
              ? 'article.archive'
              : vars.action === 'delete'
                ? 'article.delete'
                : vars.action === 'feature' || vars.action === 'unfeature'
                  ? 'article.featured.toggle'
                  : 'article.update',
        resource: 'article',
        summary: t('audit.bulk', { action: vars.action, count: vars.ids.length }),
        meta: { ids: vars.ids, action: vars.action },
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const runBulk = (action: BulkArticleAction, rows: ArticleListItem[]) => {
    const ids = rows.map((r) => r.id);
    if (ids.length === 0) {
      toast.error(t('toast.selectFirst'));
      return;
    }
    bulkMutation.mutate({ action, ids });
  };

  const bulkActions = useMemo(
    () =>
      isAdmin
        ? [
            { key: 'publish', label: t('bulk.publish'), onClick: (rows: ArticleListItem[]) => runBulk('publish', rows) },
            { key: 'unpublish', label: t('bulk.unpublish'), onClick: (rows: ArticleListItem[]) => runBulk('unpublish', rows) },
            { key: 'archive', label: t('bulk.archive'), onClick: (rows: ArticleListItem[]) => runBulk('archive', rows) },
            { key: 'feature', label: t('bulk.feature'), onClick: (rows: ArticleListItem[]) => runBulk('feature', rows) },
            { key: 'unfeature', label: t('bulk.unfeature'), onClick: (rows: ArticleListItem[]) => runBulk('unfeature', rows) },
            { key: 'delete', label: t('bulk.delete'), danger: true, onClick: (rows: ArticleListItem[]) => runBulk('delete', rows) },
          ]
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, bulkMutation],
  );

  const columns = useMemo(
    () => [
      {
        key: 'title',
        title: t('table.columns.title'),
        dataIndex: 'title',
        render: (_: unknown, row: ArticleListItem) => (
          <div style={{ lineHeight: 1.2, minWidth: 0 }}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                void navigate(ROUTES.ARTICLE_EDIT.replace(':id', row.id));
              }}
              style={{
                padding: 0,
                height: 'auto',
                fontWeight: 600,
                color: 'var(--ds-text-primary)',
                whiteSpace: 'normal',
                textAlign: 'left',
              }}
            >
              {row.isFeatured ? <span aria-label="featured">⭐ </span> : null}
              {row.title}
            </Button>
            <div
              style={{
                fontSize: 12,
                color: 'var(--ds-text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 360,
              }}
              title={t('table.slugPath', { slug: row.slug })}
            >
              {t('table.slugPath', { slug: row.slug })}
            </div>
          </div>
        ),
      },
      {
        key: 'category',
        title: t('table.columns.category'),
        dataIndex: 'categoryId',
        width: 180,
        render: (v: string | null) =>
          v ? (
            <Tag>{categoryMap.get(v) ?? `${v.slice(0, 8)}…`}</Tag>
          ) : (
            <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
          ),
      },
      {
        key: 'author',
        title: t('table.columns.author'),
        dataIndex: 'authorId',
        width: 160,
        render: (v: string | null) =>
          v ? (
            <span style={{ fontSize: 13 }}>
              {authorMap.get(v) ?? `${v.slice(0, 8)}…`}
            </span>
          ) : (
            <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
          ),
      },
      {
        key: 'status',
        title: t('table.columns.status'),
        dataIndex: 'status',
        width: 140,
        render: (s: ArticleStatus) => (
          <Tooltip title={statusLabel(s)}>
            <span>
              <StatusBadge kind="content" status={toContentStatus(s)} />
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'viewCount',
        title: t('table.columns.viewCount'),
        dataIndex: 'viewCount',
        width: 120,
        align: 'right' as const,
        render: (n: number) => (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: n === 0 ? 'var(--ds-text-secondary)' : undefined,
            }}
          >
            {n}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        title: t('table.columns.updatedAt'),
        dataIndex: 'updatedAt',
        width: 130,
        render: (v: string) => (
          <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
            {dayjs(v).format('DD.MM.YYYY')}
          </span>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 130,
        render: (_: unknown, row: ArticleListItem) => {
          const isInactive = row.status === 'ARCHIVED' || row.status === 'DELETED';
          const canFeature = row.status === 'PUBLISHED' || row.isFeatured;
          const starLoading = pendingStarId === row.id && bulkMutation.isPending;
          return (
            <Space>
              <Tooltip title={t('actions.edit')}>
                <Button
                  size="small"
                  icon={<Pencil size={14} />}
                  aria-label={t('rowActions.editAria', { title: row.title })}
                  onClick={() => {
                    void navigate(ROUTES.ARTICLE_EDIT.replace(':id', row.id));
                  }}
                />
              </Tooltip>
              {isAdmin && !isInactive ? (
                <Tooltip
                  title={
                    !canFeature
                      ? t('feature.needsPublished')
                      : row.isFeatured
                        ? t('feature.remove')
                        : t('feature.add')
                  }
                >
                  <Button
                    size="small"
                    type={row.isFeatured ? 'primary' : 'default'}
                    icon={<Star size={14} />}
                    loading={starLoading}
                    disabled={!canFeature || (bulkMutation.isPending && !starLoading)}
                    aria-label={
                      row.isFeatured
                        ? t('feature.removeAria', { title: row.title })
                        : t('feature.addAria', { title: row.title })
                    }
                    onClick={() => {
                      setPendingStarId(row.id);
                      bulkMutation.mutate(
                        {
                          action: row.isFeatured ? 'unfeature' : 'feature',
                          ids: [row.id],
                        },
                        { onSettled: () => setPendingStarId(null) },
                      );
                    }}
                  />
                </Tooltip>
              ) : null}
              {isAdmin ? (
                <Popconfirm
                  title={row.status === 'PUBLISHED' ? t('rowActions.deletePublishedTitle') : t('rowActions.deleteTitle')}
                  description={
                    row.status === 'PUBLISHED'
                      ? t('rowActions.deletePublishedDescription', { title: row.title })
                      : t('rowActions.deleteDescription', { title: row.title })
                  }
                  okText={t('rowActions.deleteOk')}
                  okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                  cancelText={t('rowActions.deleteCancel')}
                  onConfirm={() => deleteMutation.mutate(row)}
                >
                  <Tooltip title={t('actions.delete')}>
                    <Button
                      size="small"
                      danger
                      icon={<Trash2 size={14} />}
                      aria-label={t('rowActions.deleteAria', { title: row.title })}
                    />
                  </Tooltip>
                </Popconfirm>
              ) : null}
            </Space>
          );
        },
      },
    ],
    [navigate, categoryMap, authorMap, deleteMutation, bulkMutation, isAdmin, pendingStarId, t, statusLabel],
  );

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('breadcrumb.list') }]}
        actions={
          <Space wrap>
            {isAdmin ? (
              <Button
                icon={<ArrowUpDown size={14} />}
                onClick={() => setReorderOpen(true)}
              >
                {t('actions.reorderFeatured')}
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={() => {
                void navigate(ROUTES.ARTICLE_NEW);
              }}
            >
              {t('actions.newArticle')}
            </Button>
          </Space>
        }
      />

      <Card>
        <DataTable<ArticleListItem>
          data={items}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          exportable
          exportFileName="articles"
          bulkActions={bulkActions}
          search={{
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: t('search.placeholder'),
          }}
          filters={
            <Space wrap size={isMobile ? 6 : 8} style={{ width: isMobile ? '100%' : undefined }}>
              {isMobile ? (
                <Select<StatusFilter>
                  size="small"
                  value={status}
                  onChange={(v) => {
                    setStatus(v);
                    setPage(1);
                  }}
                  style={{ minWidth: 140 }}
                  options={[
                    { value: 'ALL', label: t('options.all') },
                    ...ARTICLE_FILTER_STATUSES.map((s) => ({
                      value: s,
                      label: statusLabel(s),
                    })),
                  ]}
                />
              ) : (
                <Segmented<StatusFilter>
                  size="small"
                  value={status}
                  onChange={(v) => {
                    setStatus(v);
                    setPage(1);
                  }}
                  options={[
                    { value: 'ALL', label: t('options.all') },
                    ...ARTICLE_FILTER_STATUSES.map((s) => ({
                      value: s,
                      label: statusLabel(s),
                    })),
                  ]}
                />
              )}
              <Select<string>
                size="small"
                allowClear
                placeholder={t('filters.category')}
                value={categoryId}
                onChange={(v) => {
                  setCategoryId(v);
                  setPage(1);
                }}
                style={{ minWidth: isMobile ? 140 : 180 }}
                options={(categoriesData?.items ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
              <Select<string>
                size="small"
                allowClear
                placeholder={t('filters.author')}
                value={authorId}
                onChange={(v) => {
                  setAuthorId(v);
                  setPage(1);
                }}
                style={{ minWidth: isMobile ? 140 : 180 }}
                showSearch
                optionFilterProp="label"
                options={(authorsData?.items ?? []).map((a) => ({
                  value: a.id,
                  label: a.displayName,
                }))}
              />
              <Select<string>
                size="small"
                allowClear
                placeholder={t('filters.tag')}
                value={tagId}
                onChange={(v) => {
                  setTagId(v);
                  setPage(1);
                }}
                style={{ minWidth: isMobile ? 140 : 160 }}
                showSearch
                optionFilterProp="label"
                options={(tagsData?.items ?? []).map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
              />
              <LanguagePicker
                value={languageId}
                onChange={(v) => {
                  setLanguageId(v);
                  setPage(1);
                }}
                allowClear
                size="small"
                placeholder={t('common:contentLanguage.filterAll')}
                style={{ minWidth: isMobile ? 140 : 160 }}
              />
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <Switch
                  size="small"
                  checked={featuredOnly}
                  onChange={(v) => {
                    setFeaturedOnly(v);
                    setPage(1);
                  }}
                />
                {t('filters.featuredOnly')}
              </label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <Switch
                  size="small"
                  checked={publishedOnly}
                  onChange={(v) => {
                    setPublishedOnly(v);
                    setPage(1);
                  }}
                />
                {t('filters.publishedOnly')}
              </label>
              {hasActiveFilters ? (
                <Button size="small" type="link" onClick={resetFilters}>
                  {t('filters.clear')}
                </Button>
              ) : null}
            </Space>
          }
          pagination={{
            page,
            size: pageSize,
            total,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>

      <FeaturedReorderModal
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
      />
    </div>
  );
}
